import { ReactNode, useCallback, useMemo } from "react"
import { Table, Checkbox } from "@radix-ui/themes"

export type DataTableColumn<T> = {
  /** Key of the row object to read the cell value from (also used as React key). */
  key: string
  /** Header label. */
  header: ReactNode
  /** Custom cell renderer. Defaults to `(row) => row[column.key]`. */
  cell?: (row: T) => ReactNode
  /** Per-cell inline style. */
  cellStyle?: (row: T) => React.CSSProperties | undefined
  /** Header cell width or style. */
  headerStyle?: React.CSSProperties
  /** Render as a row-header cell (semantically `<th scope="row">`, bold by default). */
  isRowHeader?: boolean
}

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[]
  data: T[]
  /** React key extractor for rows. Defaults to `(row, i) => i`. */
  rowKey?: (row: T, index: number) => string | number
  /** Click handler for a row click. */
  onRowClick?: (row: T) => void
  /** Extra className for a row. */
  rowClassName?: (row: T) => string | undefined
  /** Extra style for a row. */
  rowStyle?: (row: T) => React.CSSProperties | undefined
  /** Message to show when data is empty. */
  emptyMessage?: ReactNode
  /** Variant passed to Table.Root. Defaults to "surface". */
  variant?: "surface" | "ghost"
  /** Size passed to Table.Root. Defaults to "2". */
  size?: "1" | "2" | "3"
  /** Max height wrapper (makes the table scrollable). */
  scrollHeight?: number | string

  // ── Selection (multi-select with checkboxes) ──
  /** Enable row selection with checkboxes. Adds a leading checkbox column. */
  selectable?: boolean
  /** Set of selected row IDs. Required when `selectable` is true. */
  selectedKeys?: Set<string | number>
  /** Callback fired when the selection changes. Receives the full selected row objects. */
  onSelectionChange?: (selectedRows: T[]) => void
  /** Stable row identifier for selection tracking. Required when `selectable` is true. */
  getRowId?: (row: T) => string | number
  /** Class applied to selected rows. */
  selectedClassName?: string
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  rowClassName,
  rowStyle,
  emptyMessage = "Нет данных для отображения",
  variant = "surface",
  size = "2",
  scrollHeight,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  getRowId,
  selectedClassName = "rt-TableRow-selected",
}: DataTableProps<T>) {
  const isRowSelected = useCallback(
    (row: T) => {
      if (!selectable || !selectedKeys || !getRowId) return false
      return selectedKeys.has(getRowId(row))
    },
    [selectable, selectedKeys, getRowId],
  )

  const toggleRow = useCallback(
    (row: T) => {
      if (!onSelectionChange || !getRowId || !selectedKeys) return
      const id = getRowId(row)
      const next = new Set(selectedKeys)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      onSelectionChange(data.filter((r) => next.has(getRowId(r))))
    },
    [onSelectionChange, getRowId, selectedKeys, data],
  )

  const toggleAll = useCallback(() => {
    if (!onSelectionChange || !getRowId) return
    if (selectedKeys && data.length > 0 && selectedKeys.size === data.length) {
      onSelectionChange([])
    } else {
      onSelectionChange([...data])
    }
  }, [onSelectionChange, getRowId, selectedKeys, data])

  const allSelected =
    selectable &&
    data.length > 0 &&
    selectedKeys != null &&
    selectedKeys.size === data.length
  const someSelected =
    selectable && selectedKeys != null && selectedKeys.size > 0 && !allSelected

  const effectiveColumns = useMemo(() => {
    if (!selectable) return columns
    const checkboxColumn: DataTableColumn<T> = {
      key: "__select",
      headerStyle: { width: "auto" },
      header: (
        <Checkbox
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={() => toggleAll()}
        />
      ),
      cell: (row) => (
        <Checkbox
          checked={isRowSelected(row)}
          onCheckedChange={() => toggleRow(row)}
        />
      ),
    }
    return [checkboxColumn, ...columns]
  }, [
    selectable,
    columns,
    allSelected,
    someSelected,
    toggleAll,
    isRowSelected,
    toggleRow,
  ])

  const table = (
    <Table.Root variant={variant} size={size}>
      <Table.Header>
        <Table.Row>
          {effectiveColumns.map((col) => (
            <Table.ColumnHeaderCell key={col.key} style={col.headerStyle}>
              {col.header}
            </Table.ColumnHeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.length === 0 ? (
          <Table.Row>
            <Table.Cell
              colSpan={effectiveColumns.length}
              style={{ textAlign: "center" }}
            >
              {emptyMessage}
            </Table.Cell>
          </Table.Row>
        ) : (
          data.map((row, index) => {
            const key = rowKey ? rowKey(row, index) : index
            const selected = isRowSelected(row)
            const userClass = rowClassName?.(row) ?? ""
            const selClass = selected ? selectedClassName : ""
            return (
              <Table.Row
                key={key}
                onClick={
                  selectable
                    ? (e) => {
                        // Don't toggle when clicking the checkbox itself
                        const target = e.target as HTMLElement
                        if (target.closest('[role="checkbox"]')) return
                        // Row click = single-select (replace selection)
                        onSelectionChange?.([row])
                        onRowClick?.(row)
                      }
                    : onRowClick
                    ? () => onRowClick(row)
                    : undefined
                }
                className={`${userClass} ${selClass}`.trim() || undefined}
                style={rowStyle?.(row)}
              >
                {effectiveColumns.map((col) => (
                  <Table.Cell key={col.key} style={col.cellStyle?.(row)}>
                    {col.cell
                      ? col.cell(row)
                      : (row as Record<string, ReactNode>)[col.key] ?? ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            )
          })
        )}
      </Table.Body>
    </Table.Root>
  )

  if (scrollHeight != null) {
    return (
      <div style={{ maxHeight: scrollHeight, overflow: "auto" }}>{table}</div>
    )
  }
  return table
}
